import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';

const PrivacyPage = () => {
  return (
    <Container maxWidth="md">
      <Paper sx={{ p: { xs: 3, md: 5 }, mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy for GrooveGate
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: July 8, 2025
        </Typography>

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Data Controller
            </Typography>
            <Typography paragraph>
              The entity responsible for data processing within the meaning of the General Data Protection Regulation (GDPR) is:
            </Typography>
            <Typography paragraph>
              GrooveGate UG (haftungsbeschränkt)<br/>
              Milanweg 7<br/>
              66687 Nunkirchen<br/>
              Email: privacy@groovegate.com
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Purpose of This Privacy Policy
            </Typography>
            <Typography paragraph>
              This privacy policy informs you about which personal data we collect when users use our platform (www.groovegate.com), for what purposes and on what legal basis this occurs, and what rights you have.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Types of Data Processed
            </Typography>
            <Typography component="ul" sx={{ pl: 3 }}>
              <li>Master data (e.g., names, addresses)</li>
              <li>Contact data (e.g., email addresses, phone numbers)</li>
              <li>Contract data (e.g., usage data, subscription information)</li>
              <li>Content data (e.g., uploaded music, artwork, texts, processed for provision, distribution, or promotion according to user specifications)</li>
              <li>Usage data (e.g., visited pages, click behavior, for optimizing user experience and improving the platform)</li>
              <li>Meta/communication data (e.g., IP addresses, device information, processed for IT security and error analysis)</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Purposes of Processing
            </Typography>
            <Typography component="ul" sx={{ pl: 3 }}>
              <li>Provision of the platform, its content and functions</li>
              <li>Contract execution and customer service</li>
              <li>Sending communications:
                <ul>
                  <li>Transaction emails (e.g., login confirmation, billing information) based on contract fulfillment</li>
                  <li>Promotional emails/newsletters only with prior consent</li>
                </ul>
              </li>
              <li>Abuse and fraud detection</li>
              <li>Platform security and stability</li>
              <li>Analysis of user behavior (e.g., listener matching for recommending suitable music, label promos, curator matching – based on legitimate interest or consent for extended profiling functions)</li>
              <li>Marketing and reach measurement (only with consent)</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Legal Basis for Processing
            </Typography>
            <Typography paragraph>
              We process personal data according to Art. 6 Para. 1 GDPR:
            </Typography>
            <Typography component="ul" sx={{ pl: 3 }}>
              <li>a) With consent (e.g., for newsletter sending or tracking)</li>
              <li>b) For contract fulfillment or pre-contractual measures (e.g., creating an account)</li>
              <li>c) To fulfill legal obligations (e.g., tax retention obligations)</li>
              <li>f) To protect legitimate interests (e.g., platform operation, security, abuse prevention)</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Registration and Account Management
            </Typography>
            <Typography paragraph>
              When creating a user account, we process the provided data for account management, authentication, communication, and contract execution.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              User Content
            </Typography>
            <Typography paragraph>
              When users upload music, texts, artwork, or other content, processing occurs according to their instructions for provision, promotion, or distribution via the platform. The responsibility for lawful use and publication of this content lies with the users.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Newsletter and Promotional Function
            </Typography>
            <Typography paragraph>
              Sending promotional emails via our platform is only permitted to recipients who have expressly consented (double opt-in strongly recommended). Users who use this function are independently responsible for the lawful use of address data and proof of consent.
            </Typography>
            <Typography paragraph>
              If GrooveGate provides tracking functions such as opening or click statistics within the promotional function, this is done as a data processor on behalf of the users. The responsibility for GDPR-compliant use lies with the sender.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Data Sharing
            </Typography>
            <Typography paragraph>
              Personal data is only shared:
            </Typography>
            <Typography component="ul" sx={{ pl: 3 }}>
              <li>For contract fulfillment (e.g., to payment service providers like Stripe, hosting partners, content delivery networks)</li>
              <li>Based on consent</li>
              <li>Due to legal obligations</li>
              <li>To external service providers within the framework of order processing according to Art. 28 GDPR (e.g., IT support, newsletter services, cloud services)</li>
            </Typography>
            <Typography paragraph>
              Only data necessary for the respective fulfillment is shared (principle of data minimization).
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Use of Cookies and Analytics Tools
            </Typography>
            <Typography paragraph>
              We use cookies and similar technologies to technically operate our platform and for analysis and optimization of usage.
            </Typography>
            <Typography paragraph>
              Consent for this is obtained through our cookie banner (Consent Management Tool), provided the cookies are not technically necessary. A detailed overview of the cookies and tools used can be found in our Cookie Policy.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Storage Duration
            </Typography>
            <Typography paragraph>
              Personal data is only stored as long as necessary for the respective purposes or as required by legal retention obligations.
            </Typography>
            <Typography paragraph>
              Examples:
            </Typography>
            <Typography component="ul" sx={{ pl: 3 }}>
              <li>Account data: for the duration of the active account + 6 months after termination (backup purposes)</li>
              <li>Contract and payment data: 10 years according to tax law requirements (AO, HGB)</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              User Rights
            </Typography>
            <Typography paragraph>
              Users have the right to:
            </Typography>
            <Typography component="ul" sx={{ pl: 3 }}>
              <li>Information (Art. 15 GDPR)</li>
              <li>Correction (Art. 16 GDPR)</li>
              <li>Deletion (Art. 17 GDPR)</li>
              <li>Restriction of processing (Art. 18 GDPR)</li>
              <li>Data portability (Art. 20 GDPR)</li>
              <li>Object (Art. 21 GDPR)</li>
              <li>Withdraw consent (Art. 7 Para. 3 GDPR)</li>
              <li>Lodge a complaint with a supervisory authority (Art. 77 GDPR)</li>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              External Services and Plugins
            </Typography>
            <Typography paragraph>
              If third-party services (e.g., payment providers, hosting, newsletter tools, cloud services) are integrated, this is done exclusively in compliance with the GDPR.
            </Typography>
            <Typography paragraph>
              We provide a current list of the service providers used transparently here or upon request.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Data Security
            </Typography>
            <Typography paragraph>
              We implement appropriate technical and organizational security measures according to Art. 32 GDPR to protect personal data from loss, misuse, or unauthorized access (e.g., TLS encryption, two-factor authentication, role-based rights management).
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              Changes to This Privacy Policy
            </Typography>
            <Typography paragraph>
              We reserve the right to adapt this privacy policy to align it with changed legal situations or when changes are made to our services. Users will be informed of significant changes by email or other suitable means.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPage;
